/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import CardClient from '@atlaskit/link-provider/client';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import Spinner from '@atlaskit/spinner/spinner';
import { token } from '@atlaskit/tokens';
import { hasPersistedConfigurationChanged } from '@atlassian/ai-managed-objects/configuration';
import {
	getNextSourceEntries,
	getSourceUrlsFromEntries,
} from '@atlassian/ai-managed-objects/sources';
import type { AiManagedObject, AiManagedObjectConfig } from '@atlassian/ai-managed-objects/types';
import {
	getSourceValidationError,
	validateAiManagedObjectConfig,
	type SourceValidationError,
} from '@atlassian/ai-managed-objects/validation';

import { messages } from '../../common/messages';
import { AimoStatusMetadata } from './AimoStatusMetadata';
import { type ContentOptionValue, useConfigPanelOptions } from './config-panel-options';
import { ConfigPanel } from './ConfigPanel';
import {
	arePanelConfigurationsEqual,
	type ManagedBlockConfigSaveHandler,
	type ManagedBlockConfiguration,
	type ManagedBlockConfigurationUpdate,
	getDefaultStructuredPanelConfiguration,
	getOutputFormatForGoal,
	toAiManagedObjectConfigInput,
} from './configuration';
import { GoalSelect } from './GoalSelect';
import { ManagedBlockConfigFrame } from './ManagedBlockConfigFrame';
import { createPromptAdf, getSourceUrlsFromPromptAdf } from './prompt-adf';
import { PromptPanel } from './PromptPanel';
import type { SmartCardProviderProduct } from './sources/smart-card-product';
import { useResolveSources } from './sources/useResolveSources';
import { validationErrorMessageKeys } from './validation-error-message-keys';

const saveErrorStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.050'),
	color: token('color.text.danger'),
});

const footerStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.200'),
});

const actionsStyles = css({
	marginLeft: 'auto',
});

const promptSourceErrorStyles = css({
	color: token('color.text.danger'),
});

type StructuredSourceState = Pick<
	ManagedBlockConfiguration,
	'sourceEntries' | 'sourceMode' | 'sourceUrls'
>;

type ManagedBlockConfigState = {
	configuration: ManagedBlockConfiguration;
	showPromptSourceLimitError: boolean;
};

const applyStateAction = <T,>(currentValue: T, action: SetStateAction<T>): T =>
	typeof action === 'function' ? (action as (currentValue: T) => T)(currentValue) : action;

const getPromptSourceEntries = (configuration: ManagedBlockConfiguration) =>
	configuration.mode === 'prompt' ? configuration.sourceEntries : [];

const getStructuredSourceState = (
	configuration: ManagedBlockConfiguration,
): StructuredSourceState =>
	configuration.mode === 'configuration'
		? {
				sourceEntries: configuration.sourceEntries,
				sourceMode: configuration.sourceMode,
				sourceUrls: configuration.sourceUrls,
			}
		: {
				sourceEntries: [],
				sourceMode: configuration.sourceMode,
				sourceUrls: '',
			};

const TEXT_ENTRY_INPUT_TYPES: ReadonlySet<string> = new Set([
	'email',
	'number',
	'password',
	'search',
	'tel',
	'text',
	'url',
]);

type OwnerWindow = Window & typeof globalThis;

const isElementInWindow = (
	target: EventTarget | null,
	ownerWindow: OwnerWindow,
): target is HTMLElement => target instanceof ownerWindow.HTMLElement;

const isTextEntryTarget = (
	target: EventTarget | null,
	panelElement: HTMLElement,
	ownerWindow: OwnerWindow,
): boolean => {
	if (!isElementInWindow(target, ownerWindow)) {
		return false;
	}

	const element = target.closest('input, textarea, [contenteditable="true"], [role="textbox"]');
	if (!isElementInWindow(element, ownerWindow) || !panelElement.contains(element)) {
		return false;
	}

	if (element instanceof ownerWindow.HTMLTextAreaElement) {
		return true;
	}

	if (element instanceof ownerWindow.HTMLInputElement) {
		return TEXT_ENTRY_INPUT_TYPES.has(element.type);
	}

	return element.isContentEditable || element.getAttribute('role') === 'textbox';
};

export type ManagedBlockConfigProps = {
	/** Hydrated AI-managed object metadata for the configured block. */
	aimoData?: AiManagedObject | null;
	/** Site cloud ID, used for profilecard hydration. */
	cloudId?: string;
	/** Current Confluence page URL to use for current-page sources. */
	currentPageSourceUrl?: string;
	/** If set, an error message to display inline at the bottom of the panel. */
	error?: string | null;
	/** Initial configuration hydrated from the persisted AI-managed object. */
	initialConfiguration: ManagedBlockConfiguration;
	/** If true, the panel is currently performing an async operation. */
	isSaving?: boolean;
	/** If false, keyboard shortcut listeners are disabled for hidden panel instances. */
	isVisible?: boolean;
	/** Called when the user dismisses the panel. */
	onCancel?: () => void;
	/** Called when the user changes config after an error is shown. */
	onClearError?: () => void;
	/** Called when the user clicks save with normalized AIMO config input. */
	onSave: ManagedBlockConfigSaveHandler;
	/** Persisted configuration used to disable no-change saves. */
	persistedConfig?: AiManagedObjectConfig | null;
	/** If true, show the source disclaimer immediately when the panel first renders. */
	showInitialSourceDisclaimer?: boolean;
	/** Product context supplied to Smart Links when rendering configured source chips. */
	smartCardProviderProduct?: SmartCardProviderProduct;
};

/**
 * Inline "Configure block" panel rendered directly underneath an
 * agent-managed bodied extension.
 */
export function ManagedBlockConfig({
	aimoData = null,
	cloudId,
	currentPageSourceUrl,
	initialConfiguration,
	isVisible = true,
	onSave,
	onCancel,
	onClearError,
	isSaving = false,
	error = null,
	persistedConfig = null,
	smartCardProviderProduct,
	showInitialSourceDisclaimer = false,
}: ManagedBlockConfigProps): JSX.Element {
	const intl = useIntl();
	const { goalOptions: promptGoalOptions } = useConfigPanelOptions();
	const [configState, setConfigState] = useState<ManagedBlockConfigState>({
		configuration: initialConfiguration,
		showPromptSourceLimitError: false,
	});
	const { configuration, showPromptSourceLimitError } = configState;
	const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
	const [promptEditorInstanceKey, setPromptEditorInstanceKey] = useState(0);
	const hasEditedConfigurationRef = useRef(false);
	const hasEditedPromptRef = useRef(false);
	const lastHydratedConfigurationRef = useRef(initialConfiguration);
	const panelRef = useRef<HTMLDivElement>(null);
	const hadFocusWithinPanelRef = useRef(false);
	const hasFocusedPanelRef = useRef(false);
	const configurationRef = useRef(configuration);
	const promptSourceEntriesRef = useRef(getPromptSourceEntries(initialConfiguration));
	const structuredSourceStateRef = useRef(getStructuredSourceState(initialConfiguration));

	const updateConfiguration = useCallback((update: SetStateAction<ManagedBlockConfiguration>) => {
		setConfigState((currentState) => ({
			...currentState,
			configuration: applyStateAction(currentState.configuration, update),
		}));
	}, []);

	useEffect(() => {
		configurationRef.current = configuration;
	}, [configuration]);

	useEffect(() => {
		if (configuration.mode === 'prompt') {
			promptSourceEntriesRef.current = configuration.sourceEntries;
			return;
		}

		structuredSourceStateRef.current = getStructuredSourceState(configuration);
	}, [configuration]);

	useEffect(() => {
		const currentConfiguration = configurationRef.current;
		const lastHydratedConfiguration = lastHydratedConfigurationRef.current;
		lastHydratedConfigurationRef.current = initialConfiguration;

		if (
			arePanelConfigurationsEqual(
				currentConfiguration,
				lastHydratedConfiguration,
				currentPageSourceUrl,
			)
		) {
			hasEditedConfigurationRef.current = false;
			hasEditedPromptRef.current = false;
			setPromptEditorInstanceKey((currentKey) => currentKey + 1);
			updateConfiguration(initialConfiguration);
			promptSourceEntriesRef.current = getPromptSourceEntries(initialConfiguration);
			structuredSourceStateRef.current = getStructuredSourceState(initialConfiguration);
		}
	}, [currentPageSourceUrl, initialConfiguration, updateConfiguration]);

	useEffect(() => {
		if (!isVisible) {
			hasFocusedPanelRef.current = false;
			return;
		}

		if (hasFocusedPanelRef.current) {
			return;
		}

		const panelElement = panelRef.current;
		if (!panelElement) {
			return;
		}

		panelElement.focus({ preventScroll: true });
		hadFocusWithinPanelRef.current = true;
		hasFocusedPanelRef.current = true;
	}, [isVisible]);

	const clearErrorsOnInteraction = useCallback(() => {
		setHasAttemptedSave(false);
		onClearError?.();
	}, [onClearError]);

	const markConfigurationEdited = useCallback(() => {
		hasEditedConfigurationRef.current = true;
	}, []);

	const handleConfigurationChange = useCallback(
		(update: ManagedBlockConfigurationUpdate) => {
			updateConfiguration(update);
		},
		[updateConfiguration],
	);

	const promptCardClient = useMemo(
		() => (configuration.mode === 'prompt' ? new CardClient() : null),
		[configuration.mode],
	);

	useResolveSources({
		cardClient: promptCardClient,
		onChange: handleConfigurationChange,
		sourceEntries: configuration.mode === 'prompt' ? configuration.sourceEntries : [],
	});

	const handlePromptToggle = useCallback(() => {
		const currentMode = configurationRef.current.mode;
		const shouldResetConfiguration = currentMode === 'prompt' && hasEditedPromptRef.current;

		clearErrorsOnInteraction();
		updateConfiguration((currentConfiguration) => {
			if (currentConfiguration.mode === 'prompt') {
				if (shouldResetConfiguration) {
					setPromptEditorInstanceKey((currentKey) => currentKey + 1);
					return getDefaultStructuredPanelConfiguration(currentConfiguration);
				}

				return {
					...currentConfiguration,
					...structuredSourceStateRef.current,
					mode: 'configuration',
				};
			}

			return {
				...currentConfiguration,
				mode: 'prompt',
				sourceEntries: promptSourceEntriesRef.current,
				sourceUrls: getSourceUrlsFromEntries(promptSourceEntriesRef.current),
			};
		});

		if (shouldResetConfiguration) {
			hasEditedConfigurationRef.current = false;
			hasEditedPromptRef.current = false;
		}
	}, [clearErrorsOnInteraction, updateConfiguration]);

	const handlePromptChange = useCallback(
		(nextPrompt: string) => {
			clearErrorsOnInteraction();

			if (configurationRef.current.prompt !== nextPrompt) {
				hasEditedPromptRef.current = true;
			}

			updateConfiguration((currentConfiguration) => ({
				...currentConfiguration,
				prompt: nextPrompt,
			}));
		},
		[clearErrorsOnInteraction, updateConfiguration],
	);

	const handlePromptAdfChange = useCallback(
		(nextPromptAdf: JSONDocNode) => {
			const sourceUrls = getSourceUrlsFromPromptAdf(nextPromptAdf);
			const sourceUrlSet = new Set(sourceUrls);

			clearErrorsOnInteraction();
			hasEditedPromptRef.current = true;

			setConfigState((currentState) => {
				const currentConfiguration = currentState.configuration;
				const nextRetainedSourceEntries = currentConfiguration.sourceEntries.filter((source) =>
					sourceUrlSet.has(source.url),
				);
				const { entries: nextSourceEntries, limitExceeded } = getNextSourceEntries(
					nextRetainedSourceEntries,
					sourceUrls.join('\n'),
				);

				return {
					...currentState,
					configuration: {
						...currentConfiguration,
						sourceEntries: nextSourceEntries,
						sourceMode: 'manual',
						sourceUrls: getSourceUrlsFromEntries(nextSourceEntries),
					},
					showPromptSourceLimitError: limitExceeded,
				};
			});
		},
		[clearErrorsOnInteraction],
	);

	const handlePromptContentSelectionChange = useCallback(
		(nextSelection: ContentOptionValue) => {
			if (nextSelection === 'prompt') {
				return;
			}

			const shouldResetConfiguration =
				configurationRef.current.mode === 'prompt' && hasEditedPromptRef.current;

			clearErrorsOnInteraction();

			updateConfiguration((currentConfiguration) => {
				const baseConfiguration = shouldResetConfiguration
					? getDefaultStructuredPanelConfiguration(currentConfiguration)
					: {
							...currentConfiguration,
							...structuredSourceStateRef.current,
							mode: 'configuration' as const,
						};
				const nextOutputFormat = getOutputFormatForGoal(
					nextSelection,
					baseConfiguration.outputFormat,
				);

				if (
					baseConfiguration.goal !== nextSelection ||
					baseConfiguration.outputFormat !== nextOutputFormat
				) {
					hasEditedConfigurationRef.current = true;
				}

				return {
					...baseConfiguration,
					goal: nextSelection,
					outputFormat: nextOutputFormat,
				};
			});

			if (shouldResetConfiguration) {
				hasEditedPromptRef.current = false;
			}
		},
		[clearErrorsOnInteraction, updateConfiguration],
	);

	const configInput = useMemo(
		() => toAiManagedObjectConfigInput(configuration, currentPageSourceUrl),
		[configuration, currentPageSourceUrl],
	);
	const validation = useMemo(() => validateAiManagedObjectConfig(configInput), [configInput]);
	const manualSourceValidationError = useMemo<SourceValidationError>(
		() =>
			configuration.mode === 'configuration' && configuration.sourceMode === 'manual'
				? getSourceValidationError({
						requireSource: true,
						sourceEntries: configuration.sourceEntries,
					})
				: null,
		[configuration.mode, configuration.sourceEntries, configuration.sourceMode],
	);
	const promptSourceValidationError = useMemo<SourceValidationError>(
		() =>
			configuration.mode === 'prompt'
				? getSourceValidationError({
						requireSource: false,
						sourceEntries: configuration.sourceEntries,
					})
				: null,
		[configuration.mode, configuration.sourceEntries],
	);
	const sourceValidationError =
		hasAttemptedSave &&
		configuration.mode === 'configuration' &&
		configuration.sourceMode === 'manual'
			? manualSourceValidationError
			: null;
	const promptSourceError =
		hasAttemptedSave && configuration.mode === 'prompt' ? promptSourceValidationError : null;
	const promptSourceErrorMessageKey = showPromptSourceLimitError
		? 'sourceLimitError'
		: promptSourceError
			? validationErrorMessageKeys[promptSourceError]
			: null;
	const isAdditionalContextInvalid =
		configuration.mode === 'configuration' &&
		configuration.isAdditionalContextSelected &&
		configuration.additionalContext.trim().length === 0;
	const additionalContextValidationError = hasAttemptedSave && isAdditionalContextInvalid;
	const promptValidationError =
		hasAttemptedSave && configuration.mode === 'prompt' && configuration.prompt.trim().length === 0;
	const promptDefaultValue = useMemo(
		() =>
			configuration.mode === 'prompt'
				? createPromptAdf(configuration.prompt, configuration.sourceEntries)
				: undefined,
		[configuration.mode, configuration.prompt, configuration.sourceEntries],
	);
	const hasConfigurationChanged = hasPersistedConfigurationChanged(persistedConfig, configInput);
	const isSaveDisabled = isSaving || !hasConfigurationChanged;

	const handleSave = useCallback(() => {
		if (isSaveDisabled) {
			return;
		}

		setHasAttemptedSave(true);

		if (
			manualSourceValidationError ||
			promptSourceValidationError ||
			isAdditionalContextInvalid ||
			!validation.valid
		) {
			return;
		}

		void onSave(configInput);
	}, [
		configInput,
		isAdditionalContextInvalid,
		isSaveDisabled,
		manualSourceValidationError,
		onSave,
		promptSourceValidationError,
		validation,
	]);

	const handleCancel = useCallback(() => {
		onCancel?.();
	}, [onCancel]);

	useEffect(() => {
		if (!isVisible) {
			return;
		}

		const panelElement = panelRef.current;
		const ownerWindow = panelElement?.ownerDocument.defaultView as OwnerWindow | null;
		if (!panelElement || !ownerWindow) {
			return;
		}

		const stopShortcutPropagation = (event: KeyboardEvent, shouldPreventDefault = false) => {
			if (shouldPreventDefault) {
				event.preventDefault();
			}
			event.stopPropagation();
			event.stopImmediatePropagation();
		};

		const isTargetInsidePanel = (target: EventTarget | null): boolean =>
			target instanceof ownerWindow.Node && panelElement.contains(target);

		const handleFocusIn = (event: FocusEvent): void => {
			hadFocusWithinPanelRef.current = isTargetInsidePanel(event.target);
		};

		const handlePointerDown = (event: PointerEvent): void => {
			hadFocusWithinPanelRef.current = isTargetInsidePanel(event.target);
		};

		const handleKeyDown = (event: KeyboardEvent) => {
			const eventTarget = event.target;
			const isInsidePanel = isTargetInsidePanel(eventTarget);
			const isDocumentFocusFallback =
				hadFocusWithinPanelRef.current &&
				ownerWindow.document.activeElement === ownerWindow.document.body &&
				(eventTarget === ownerWindow.document.body || eventTarget === ownerWindow.document);

			if (!isInsidePanel && !isDocumentFocusFallback) {
				return;
			}

			const isCloseShortcut = event.key === 'Escape' || event.key === 'Esc';
			const isTextEntry =
				isInsidePanel && isTextEntryTarget(eventTarget, panelElement, ownerWindow);
			const isModifiedEnter = event.key === 'Enter' && (event.metaKey || event.ctrlKey);
			const isPlainEnter =
				event.key === 'Enter' &&
				!event.altKey &&
				!event.ctrlKey &&
				!event.metaKey &&
				!event.shiftKey;
			const isSaveShortcut = isTextEntry ? isModifiedEnter : isPlainEnter;
			const shouldSuppressModifiedEnter = !isTextEntry && isModifiedEnter;

			if (!isSaveShortcut && !isCloseShortcut && !shouldSuppressModifiedEnter) {
				return;
			}

			if (isCloseShortcut) {
				stopShortcutPropagation(event);
				if (!isSaving) {
					handleCancel();
				}
				return;
			}

			if (shouldSuppressModifiedEnter) {
				stopShortcutPropagation(event);
				return;
			}

			stopShortcutPropagation(event, true);
			handleSave();
		};

		ownerWindow.addEventListener('focusin', handleFocusIn, true);
		ownerWindow.addEventListener('pointerdown', handlePointerDown, true);
		ownerWindow.addEventListener('keydown', handleKeyDown, true);

		return () => {
			ownerWindow.removeEventListener('focusin', handleFocusIn, true);
			ownerWindow.removeEventListener('pointerdown', handlePointerDown, true);
			ownerWindow.removeEventListener('keydown', handleKeyDown, true);
		};
	}, [handleCancel, handleSave, isSaving, isVisible]);

	return (
		<ManagedBlockConfigFrame ref={panelRef} testId="agent-managed-extension-configure-panel">
			{configuration.mode === 'configuration' ? (
				<ConfigPanel
					additionalContextValidationError={additionalContextValidationError}
					configuration={configuration}
					isDisabled={isSaving}
					onChange={handleConfigurationChange}
					onConfigEdit={markConfigurationEdited}
					onInteraction={clearErrorsOnInteraction}
					onPromptSelect={handlePromptToggle}
					showInitialSourceDisclaimer={showInitialSourceDisclaimer}
					smartCardProviderProduct={smartCardProviderProduct}
					sourceValidationError={sourceValidationError}
				/>
			) : (
				<Stack space="space.150">
					<GoalSelect
						contentSelection="prompt"
						isDisabled={isSaving}
						onContentSelectionChange={handlePromptContentSelectionChange}
						options={promptGoalOptions}
					/>
					<PromptPanel
						cardClient={promptCardClient ?? undefined}
						defaultValue={promptDefaultValue}
						isDisabled={isSaving}
						isExpanded
						onPromptADFChange={handlePromptAdfChange}
						onPromptChange={handlePromptChange}
						promptEditorKey={`agent-managed-extension-prompt-${promptEditorInstanceKey}`}
						promptValidationError={promptValidationError}
						showToggle={false}
						smartCardProviderProduct={smartCardProviderProduct}
					/>
					{promptSourceErrorMessageKey && (
						<div css={promptSourceErrorStyles} role="alert">
							<Text color="color.text.danger">
								{intl.formatMessage(messages[promptSourceErrorMessageKey])}
							</Text>
						</div>
					)}
				</Stack>
			)}

			{error && (
				<div css={saveErrorStyles} data-testid="agent-managed-extension-error" role="alert">
					<StatusErrorIcon label="" color="currentColor" size="small" />
					<Text color="color.text.danger">{error}</Text>
				</div>
			)}
			<div css={footerStyles}>
				<AimoStatusMetadata aimoData={aimoData} cloudId={cloudId} />
				<div css={actionsStyles}>
					<Inline space="space.100" alignInline="end">
						{onCancel && (
							<Button appearance="subtle" onClick={handleCancel} isDisabled={isSaving}>
								{intl.formatMessage(messages.cancel)}
							</Button>
						)}
						<Button
							appearance="primary"
							testId="agent-managed-extension-save"
							onClick={handleSave}
							isDisabled={isSaveDisabled}
							iconAfter={isSaving ? () => <Spinner size="small" /> : undefined}
						>
							{intl.formatMessage(messages.save)}
						</Button>
					</Inline>
				</div>
			</div>
		</ManagedBlockConfigFrame>
	);
}
